import React, { useEffect, useMemo, useState } from 'react';
import { findLastIndex } from 'lodash';

import Button from '../../library/button/button';
import Image from '../../library/image/image';
import Typography from '../../library/typography/typography';
import {
  ChevronLeftRegularIcon,
  PlusRegularIcon
} from '../../shared/icons/icons';

import usePlpProducts from '../../pages/product-list-page/hooks/use-plp-products';
import {
  useCompareProducts,
  useFitment,
  useProductQuantity
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getDynamicTrackActionPrefix } from '../../shared/utils/product/product';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import { ProductData, ProductTypeEnum } from '../../shared/types/graphql-types';
import { PageTypes } from '../../shared/constants/page-types';

import './product-compare-oe-card.scss';

interface ProductCompareOeCardProps {
  currentIndex: number;
  isCompareDrawerFlow?: boolean;
  isFitmentSearch?: boolean;
  isPackageSearch?: boolean;
  isSizeSearch?: boolean;
  isStaggeredSearch?: boolean;
  onAddToCompare?: (trackActionId?: string) => void;
  pageType: keyof typeof PageTypes;
  productType?: string;
  queryType?: string;
}

function ProductCompareOeCard(props: ProductCompareOeCardProps) {
  const {
    currentIndex,
    isCompareDrawerFlow = false,
    isFitmentSearch = false,
    isPackageSearch = false,
    isSizeSearch = false,
    isStaggeredSearch = false,
    onAddToCompare,
    pageType,
    productType,
    queryType
  } = props;

  const [showMultipleOeOptions, setShowMultipleOeOptions] = useState(false);

  const { onProductComparison, productsForComparison } = useCompareProducts();
  const { isFitmentLoading, selectedFitment } = useFitment();

  const { fetchOEProducts, oeProductsData } = usePlpProducts({
    isFitmentLoading,
    isFitmentSearch,
    isPackageSearch,
    isSizeSearch,
    isStaggeredSearch,
    productType,
    queryType,
    selectedFitment
  });
  const { isOE, make } = selectedFitment || {};

  const analyticsPrefix = getDynamicTrackActionPrefix(
    pageType,
    isCompareDrawerFlow
  );
  const normalizedProductType = ProductTypeEnum.Tires;
  const { quantity, rearQuantity } = useProductQuantity({
    productType: normalizedProductType
  });

  const filteredOeProducts = useMemo(
    () =>
      oeProductsData?.filter(
        (oeProduct: ProductData) =>
          !productsForComparison.some(
            product => product.code === oeProduct?.code
          )
      ),
    [oeProductsData, productsForComparison]
  );

  const lastFilledIndex = useMemo(
    () =>
      findLastIndex(
        productsForComparison,
        item => item && item?.code?.trim() !== ''
      ),
    [productsForComparison]
  );

  const showOeLink = useMemo(() => {
    const selectedIndex = isCompareDrawerFlow
      ? currentIndex === lastFilledIndex - 1
      : currentIndex === lastFilledIndex + 1;
    return (
      (selectedIndex || currentIndex === 0) &&
      isOE &&
      isArrayWithLength(filteredOeProducts)
    );
  }, [
    currentIndex,
    filteredOeProducts,
    isCompareDrawerFlow,
    isOE,
    lastFilledIndex
  ]);

  function handleOnClick(product) {
    const isSelectedForComparison =
      product &&
      productsForComparison?.some(
        p =>
          p?.code === product?.code &&
          p?.rearProduct?.code === product?.rearProduct?.code
      );
    onProductComparison(
      product,
      !isSelectedForComparison,
      quantity,
      rearQuantity
    );
    setShowMultipleOeOptions(false);
  }

  useEffect(() => {
    if (filteredOeProducts?.length <= 0 && isOE) {
      fetchOEProducts();
    }
  }, [fetchOEProducts, isOE, filteredOeProducts]);

  return (
    <>
      {showOeLink ? (
        <>
          {showMultipleOeOptions ? (
            <div
              styleName={isCompareDrawerFlow ? 'container-drawer' : 'container'}
            >
              <div
                styleName={isCompareDrawerFlow ? 'container-with-angle-bg' : ''}
              >
                {isCompareDrawerFlow && (
                  <Image
                    alt="icon-angle"
                    src="https://discounttire.scene7.com/is/content/discounttire/logos/backgrounds/card_light_gray_curve.svg"
                    styleName="icon-angle"
                  />
                )}
                <div
                  styleName={
                    isCompareDrawerFlow
                      ? 'oe-option-title-drawer'
                      : 'oe-option-title'
                  }
                >
                  <ChevronLeftRegularIcon
                    onClick={() =>
                      setShowMultipleOeOptions(!showMultipleOeOptions)
                    }
                    styleName="back-icon"
                  />
                  {isCompareDrawerFlow && (
                    <Typography styleName="title" tag="div">
                      Select your original {make} tire to compare
                    </Typography>
                  )}
                </div>
              </div>

              <div
                styleName={
                  isCompareDrawerFlow
                    ? 'drawer-option-container'
                    : 'option-container'
                }
              >
                {filteredOeProducts.map((product: ProductData) => (
                  <Typography
                    key={product.code}
                    onClick={() => {
                      setShowMultipleOeOptions(prev => !prev);
                      handleOnClick(product);
                    }}
                    styleName={
                      isCompareDrawerFlow ? 'oe-option-drawer' : 'oe-option'
                    }
                    tag="div"
                  >
                    <Typography size="extraSmall" styleName="brand" tag="div">
                      {product?.brand}
                    </Typography>
                    <Typography styleName="name" tag="div" weight="bold">
                      {product?.name}
                    </Typography>
                  </Typography>
                ))}
              </div>
            </div>
          ) : (
            <Typography
              aria-label="Add oe tire"
              size="small"
              styleName="placeholder-image"
              tag="div"
            >
              <div styleName="link-container">
                {' '}
                <PlusRegularIcon styleName="plus-icon" />{' '}
                <Button
                  onClick={() => {
                    const showMultiple =
                      filteredOeProducts?.length > 1 && !showMultipleOeOptions;

                    if (showMultiple) {
                      setShowMultipleOeOptions(prev => !prev);
                    } else {
                      handleOnClick(filteredOeProducts[0]);
                    }

                    trackAction(
                      `${analyticsPrefix}_add_oem${
                        showMultiple ? '_multiple' : ''
                      }`
                    );
                  }}
                  size="regular"
                  styleName="oe-link"
                >
                  Add your original {make} tire
                </Button>
              </div>
            </Typography>
          )}
        </>
      ) : (
        <>
          {isCompareDrawerFlow ? (
            <div styleName="add-tire-container">
              <Button
                onClick={() => {
                  onAddToCompare?.(`${analyticsPrefix}_add_from_plp`);
                }}
                styleName="add-tire-btn"
              >
                <div>
                  <PlusRegularIcon />
                  <Typography styleName="add-tire-title">
                    {' '}
                    Add to compare
                  </Typography>
                </div>
              </Button>
            </div>
          ) : (
            <Typography
              aria-label="Add to compare"
              size="small"
              styleName="placeholder-image"
              tag="div"
            >
              <PlusRegularIcon styleName="plus-icon" /> Add to compare
            </Typography>
          )}
        </>
      )}
    </>
  );
}

export default ProductCompareOeCard;
